import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useTranslation } from '../hooks/useTranslation';
import { useLinkLanguage } from '../hooks/useLinkLanguage';
import { authErrorMessage } from '../utils/authErrors';
import { redirectToGoogleAuth } from '../utils/googleAuth';
import { AuthShell } from './auth/AuthShell';
import { GoogleButton } from './auth/GoogleButton';

const API = process.env.REACT_APP_BACKEND_URL;

const nextDestination = () => {
  const next = new URLSearchParams(window.location.search).get('next');
  return next && next.startsWith('/') ? next : '/dashboard';
};

// Iter 123 — page /login 100 % i18n (6 langues) ; erreurs serveur traduites via X-Error-Code.
const PremiumLogin = () => {
  const { t, lang } = useTranslation();
  useLinkLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          await axios.get(`${API}/api/auth/me`);
          window.location.href = nextDestination();
          return;
        } catch (error) {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setCheckingAuth(false);
    };
    checkAuth();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = t('rApp.login.errors.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = t('rApp.login.errors.emailInvalid');
    if (!password) newErrors.password = t('rApp.login.errors.passwordRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/login`, { email, password });
      const token = response.data.access_token;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      toast.success(t('rApp.login.toastSuccess'));
      window.location.href = nextDestination();
    } catch (error) {
      toast.error(authErrorMessage(error, t, 'rApp.login.errors.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-testid="login-checking-session" aria-label={t('rApp.login.checkingSession')}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <AuthShell testId="premium-login" mobileLogoTestId="login-mobile-logo">
      <div className="auth-form-header">
        <h2 data-testid="login-title">{t('rApp.login.headerTitle')}</h2>
        <p data-testid="login-subtitle">{t('rApp.login.headerSubtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <div className="form-group">
          <Label htmlFor="email">{t('rApp.login.labels.email')}</Label>
          <div className="input-wrapper">
            <Mail className="input-icon" size={18} />
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder={t('rApp.login.placeholders.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={errors.email ? 'error' : ''}
              data-testid="login-email"
            />
          </div>
          {errors.email && <span className="error-text" data-testid="login-email-error">{errors.email}</span>}
        </div>

        <div className="form-group">
          <Label htmlFor="password">{t('rApp.login.labels.password')}</Label>
          <div className="input-wrapper">
            <Lock className="input-icon" size={18} />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder={t('rApp.login.placeholders.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={errors.password ? 'error' : ''}
              data-testid="login-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('rApp.login.hidePassword') : t('rApp.login.showPassword')}
              data-testid="login-password-toggle"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && <span className="error-text" data-testid="login-password-error">{errors.password}</span>}
        </div>

        <div className="form-options form-options-end">
          <Link to={`/forgot-password?lang=${lang}`} className="forgot-link" data-testid="login-forgot-link">
            {t('rApp.login.forgot')}
          </Link>
        </div>

        <Button type="submit" className="submit-btn" disabled={loading} data-testid="login-submit">
          {loading ? t('rApp.login.submitLoading') : t('rApp.login.submit')}
          <ArrowRight size={18} />
        </Button>
      </form>

      <div className="auth-divider">
        <span data-testid="login-divider">{t('rApp.login.divider')}</span>
      </div>

      <GoogleButton label={t('rApp.login.googleCta')} onClick={() => redirectToGoogleAuth(lang)} testId="google-login" />

      <p className="auth-footer-text" data-testid="login-footer">
        {t('rApp.login.noAccount')} <Link to="/register" data-testid="login-signup-link">{t('rApp.login.signUp')}</Link>
      </p>
    </AuthShell>
  );
};

export default PremiumLogin;
